import { type Href, useFocusEffect, useRouter } from "expo-router";
import { useAtomValue } from "jotai";
import { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  SectionList,
  StyleSheet,
  View,
} from "react-native";

import { sessionAtom } from "@/atoms/session";
import { EventCard } from "@/components/EventCard";
import { GText } from "@/components/g-text";
import SignInModal from "@/components/sign-in/sign-in-modal";
import { colors } from "@/constants/colors";
import { useEventList } from "@/hooks/use-event-list";
import { apolloClient } from "@/utils/apollo";
import Bugsnag from "@/utils/bugsnag";
import { generateUniqueSlug } from "@/utils/slug";
import { supabase } from "@/utils/supabase";

export default function HomeScreen() {
  const router = useRouter();
  const { loading, past, refetch, upcoming } = useEventList();
  const sessionValue = useAtomValue(sessionAtom);

  useFocusEffect(
    useCallback(() => {
      Bugsnag.leaveBreadcrumb("refreshing events");
      refetch();
    }, [refetch]),
  );
  const authLoading = sessionValue === "loading";
  const session = sessionValue === "loading" ? null : sessionValue;
  const [signInVisible, setSignInVisible] = useState(false);

  const sections = useMemo(
    () =>
      [
        { data: upcoming, title: "upcoming events" },
        ...(past.length > 0 ? [{ data: past, title: "past events" }] : []),
      ] as const,
    [upcoming, past],
  );

  return (
    <View style={styles.container}>
      <SectionList
        contentContainerStyle={[
          styles.list,
          styles.listContent,
          {
            maxWidth: 600,
            paddingBottom: 80,
          },
        ]}
        keyExtractor={(e) => e.id}
        ListEmptyComponent={
          !loading ? (
            <View style={styles.center}>
              <GText style={styles.emptyText}>no events yet</GText>
            </View>
          ) : null
        }
        ListHeaderComponent={
          loading ? (
            <View style={styles.center}>
              <ActivityIndicator color={colors.light} size="large" />
            </View>
          ) : null
        }
        renderItem={({ item }) => (
          <EventCard event={item} showStatus={item.isAdmin} />
        )}
        renderSectionHeader={({ section }) => (
          <GText style={styles.heading}>{section.title}</GText>
        )}
        sections={sections}
        showsVerticalScrollIndicator={false}
      />

      {/* this is the logout header in the top right */}
      <View style={[styles.header]}>
        <Pressable
          disabled={authLoading || !session}
          onPress={async () => {
            await supabase.auth.signOut();
            apolloClient.resetStore();
          }}
          style={[styles.logoutButton, !session && { opacity: 0 }]}
        >
          <GText style={styles.logoutText}>logout</GText>
        </Pressable>
      </View>

      {!authLoading && (
        <View style={[styles.createContainer, { paddingBottom: 12 }]}>
          {session ? (
            <Pressable
              onPress={async () => {
                const timezone =
                  Intl.DateTimeFormat().resolvedOptions().timeZone;
                const slug = await generateUniqueSlug();
                const { data, error } = await supabase
                  .from("event")
                  .insert({
                    created_by_user_id: session?.user.id,
                    slug,
                    status: "draft",
                    timezone,
                  })
                  .select("slug")
                  .single();

                Bugsnag.leaveBreadcrumb("direct insert result", {
                  error: String(error),
                  slug: data?.slug ?? "",
                });
                if (!data) return;
                router.push(`/e/${data.slug}?mode=edit` as Href);
              }}
              style={styles.createButton}
            >
              <GText style={styles.createText}>+ create event</GText>
            </Pressable>
          ) : (
            <Pressable
              onPress={() => setSignInVisible(true)}
              style={styles.createButton}
            >
              <GText style={styles.createText}>sign in</GText>
            </Pressable>
          )}
        </View>
      )}

      <SignInModal
        onClose={() => setSignInVisible(false)}
        onSignedIn={() => setSignInVisible(false)}
        visible={signInVisible}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  center: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  container: {
    backgroundColor: colors.transparent,
    flex: 1,
  },
  createButton: {
    backgroundColor: colors.white,
    borderRadius: 28,
    paddingHorizontal: 28,
    paddingVertical: 14,
  },
  createContainer: {
    alignItems: "center",
    backgroundColor: colors.transparent,
    bottom: 0,
    left: 0,
    paddingTop: 12,
    position: "absolute",
    right: 0,
  },
  createText: {
    color: colors.black,
    fontSize: 16,
    fontWeight: "700",
  },
  emptyText: {
    color: colors.light,
    fontSize: 16,
  },
  header: {
    position: "absolute",
    right: 0,
    top: 0,
  },
  heading: {
    color: colors.light,
    flex: 1,
    fontSize: 32,
    fontWeight: "800",
    paddingVertical: 20,
    textAlign: "center",
  },
  list: {
    paddingHorizontal: 24,
  },
  listContent: {
    alignSelf: "center",
    width: "100%",
  },
  logoutButton: {
    alignItems: "flex-end",
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  logoutText: {
    color: colors.light,
    fontSize: 14,
  },
});
